import cn from "classnames";
import { useMouseEvents } from "components/shared/tooltipFME";
import { itemChangeTitle } from "helpers/bid";
import { formatDateTimezone } from "helpers/bidpoint";
import { dateTimeToView, humanizedTimeDiff } from "helpers/date";
import { bidPointTypes, bidPointTypesEnum } from "helpers/enums";
import { enumLabel, formatNumber } from "helpers/system";
import compact from "lodash/compact";
import upperFirst from "lodash/upperFirst";
import moment from "moment";
import React, { useState } from "react";
import { Popover } from "react-tiny-popover";
import CarDropd from "../../city/cell/car/dropd";
import { useValue } from "../../realtime";
import { pointFactEndDate, pointFactStartDate } from "../dateValues";
import { carPlanningSubgroups, carPlanningValueModesEnum } from "../valueModes";

const DROPD_POPOVER_STYLE = { zIndex: "1000000" };
const emptyMileageSubgroups = [
    carPlanningSubgroups.EmptyMileagePlan,
    carPlanningSubgroups.EmptyMileageFact,
    carPlanningSubgroups.EmptyMileageFactFallbackPlan,
];

export default function RangeItem(props) {
    const { item, options } = props;

    const realtimeItem = useValue({
        listenEventNames: [
            ...(item.bidData?.bidId && item.car?.id ? [`items[${item.bidData?.bidId}&${item.car?.id}]`] : []),
            ...(item.car?.id ? [`items[car.id(${item.car?.id})]`] : []),
        ],
        originalValue: item,
    });

    const [openedDropd, setOpenedDropd] = useState(false);
    const [dropdAnchor, setDropdAnchor] = useState();
    const showContentInNewDesign =
        realtimeItem.pointType === "plan" || options.valueMode === carPlanningValueModesEnum.Fact;

    const mouseEvents = useMouseEvents({
        overlay: rangeTooltip(realtimeItem, options.organization),
        timeout: 300,
        className: "car-planning__range-tooltip",
    });

    const events =
        realtimeItem.subgroup === carPlanningSubgroups.Fact &&
        options.valueMode === carPlanningValueModesEnum.PlanAndFact
            ? {}
            : mouseEvents;

    const dropdProps = {
        item: {
            ...realtimeItem,
            carId: realtimeItem?.car?.id,
            carPlanningComment: realtimeItem?.planningComment,
            number: realtimeItem?.number,
            bidId: realtimeItem.bidData.bidId,
            hasBid: true,
        },
        uncontrolled: true,
        bidSelected: true,
        ...options,
    };

    const openDropd = (event) => {
        if (openedDropd) {
            return;
        }
        setDropdAnchor({
            x: event.clientX,
            y: event.clientY,
        });
        setOpenedDropd(true);
        setTimeout(() => document.addEventListener("click", closeDropd), 0);
    };

    const closeDropd = () => {
        setOpenedDropd(false);
        setDropdAnchor(undefined);
        document.removeEventListener("click", closeDropd);
    };

    const enterUnloadDateTime = () => {
        const isFact = realtimeItem.pointType === "fact";
        const date = isFact
            ? pointFactEndDate(realtimeItem.endPoint)
            : realtimeItem.endPoint.planLeaveDateOffset || realtimeItem.endPoint.planEnterDateOffset;

        return (
            <small>
                {dateTimeToView(date, "DD.MM HH:mm")} ({isFact ? "факт" : "план"})
            </small>
        );
    };

    const enterLoadDateTime = () => {
        const isFact = realtimeItem.pointType === "fact";
        const date = isFact ? pointFactStartDate(realtimeItem.startPoint) : realtimeItem.startPoint.planEnterDateOffset;

        return (
            <small className="pr-3">
                {dateTimeToView(date, "DD.MM HH:mm")} ({isFact ? "факт" : "план"})
            </small>
        );
    };

    const dropdContent = (
        <div onClick={closeDropd}>
            <CarDropd {...dropdProps} />
        </div>
    );

    const dropdTransform = ({ popoverRect, boundaryRect }) => {
        const inset = 8;
        const gap = 1;
        const maxLeft = Math.max(boundaryRect.left + inset, boundaryRect.right - popoverRect.width - inset);
        const maxTop = Math.max(boundaryRect.top + inset, boundaryRect.bottom - popoverRect.height - inset);
        const topAbove = dropdAnchor.y - popoverRect.height - gap;
        const topBelow = dropdAnchor.y + gap;
        const top = topAbove >= boundaryRect.top + inset ? topAbove : topBelow;

        return {
            left: clamp(dropdAnchor.x, boundaryRect.left + inset, maxLeft),
            top: clamp(top, boundaryRect.top + inset, maxTop),
        };
    };

    return (
        <Popover
            isOpen={openedDropd}
            positions={["top", "bottom", "right", "left"]}
            align="start"
            transform={dropdTransform}
            content={dropdContent}
            containerStyle={DROPD_POPOVER_STYLE}
        >
            <div
                className="d-flex justify-content-between font-size-12 car-planning__item"
                {...events}
                onClick={openDropd}
            >
                <div
                    className={cn({
                        "car-planning__item--start-icon": [bidPointTypesEnum.ServicePoint, undefined].includes(
                            realtimeItem.startPoint.type,
                        ),
                        "hidden-in-new-planning": !showContentInNewDesign,
                    })}
                >
                    {!options.hideLoadPoints && (
                        <>
                            {realtimeItem.startPoint.geozone.city}
                            <span className="show-only-new-planning">
                                <>
                                    <br />
                                    {enterLoadDateTime()}
                                </>
                            </span>
                        </>
                    )}
                </div>
                <div
                    className={cn("text-right", {
                        "car-planning__item--end-icon": [bidPointTypesEnum.ServicePoint, undefined].includes(
                            realtimeItem.endPoint.type,
                        ),
                        "hidden-in-new-planning": !showContentInNewDesign,
                    })}
                >
                    {realtimeItem.endPoint.geozone.city}
                    <span className="show-only-new-planning">
                        <>
                            <br />
                            {enterUnloadDateTime()}
                        </>
                    </span>
                </div>
            </div>
        </Popover>
    );
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const rangeTooltip = (item, organization) => {
    const isEmptyMileage = emptyMileageSubgroups.includes(item.subgroup);

    const pointAddress = (geozone) => {
        const { city, village, county } = geozone;
        return city || village || county;
    };

    const timeAtPointLabel = (type) => {
        switch (type) {
            case bidPointTypesEnum.LoadPoint:
                return "Время загрузки";
            case bidPointTypesEnum.UnloadPoint:
                return "Время выгрузки";
            case bidPointTypesEnum.ServicePoint:
                return "Время работ";
            case bidPointTypesEnum.CustomPoint:
                return "Время нахождения на точке";
            case undefined:
                return "Время перецепки/пересменки";
            default: {
                return type;
            }
        }
    };

    const loadUnloadTime = (point, type) => {
        if (point.type === bidPointTypesEnum.StartPoint) {
            return "-";
        }

        const startTime = type === "plan" ? point.planEnterDateOffset : point.enteredAtByLogist || point.autoEnteredAt;
        const endTime = type === "plan" ? point.planLeaveDateOffset : point.loadUnloadedAtByLogist || point.autoLeftAt;
        if (!startTime || !endTime) {
            return "-";
        }

        return humanizedTimeDiff(endTime, startTime);
    };

    const typeTitle = (point) => {
        if (!point.type) {
            return "Перецепка/пересменка";
        }
        return point.type === bidPointTypesEnum.CustomPoint
            ? point.customPointTypeName
            : enumLabel(bidPointTypes, point.type);
    };

    const shiftItems = (point) => {
        const { shiftCar, shiftDriver, shiftSecondaryDriver, shiftTrailer } = point;
        if (!shiftCar && !shiftDriver && !shiftSecondaryDriver && !shiftTrailer) {
            return null;
        }
        if (shiftDriver) {
            shiftDriver.shortName = compact([shiftDriver.firstName, shiftDriver.lastName]).join(" ");
        }
        if (shiftSecondaryDriver) {
            shiftSecondaryDriver.shortName = compact([
                shiftSecondaryDriver.firstName,
                shiftSecondaryDriver.lastName,
            ]).join(" ");
        }
        return (
            <div className="pb-1">
                {itemChangeTitle(shiftCar, shiftDriver, shiftSecondaryDriver, shiftTrailer, { interactive: false })}
            </div>
        );
    };

    return (
        <>
            <div className="pb-2 d-flex justify-content-between align-items-baseline">
                <span className="badge badge-pill badge-success font-size-12">Заявка №{item.bidData.bidId}</span>
                {isEmptyMileage && <span className="badge badge-secondary">Порожний пробег</span>}
            </div>
            <div className="pb-2">
                <div className="car-planning__point-address-container">
                    <span className="car-planning__point-address-container--title">
                        {pointAddress(item.startPoint.geozone)}{" "}
                        <small className="text-muted">{formatDateTimezone(item.startPoint)}</small>
                    </span>
                    <small className="b-timeline-point__city--type ml-3">{typeTitle(item.startPoint)}</small>
                </div>
                {shiftItems(item.startPoint)}
                <table className="table table-xs">
                    <tbody>
                        <tr>
                            <td className="noborder">Въезд, план:</td>
                            <td className="text-right noborder">
                                {dateTimeToView(item.startPoint.planEnterDateOffset)}
                            </td>
                        </tr>
                        <tr>
                            <td>Въезд, факт:</td>
                            <td className="text-right">
                                {dateTimeToView(item.startPoint.enteredAtByLogist || item.startPoint.autoEnteredAt)}
                            </td>
                        </tr>
                        <tr>
                            <td>{item.endPoint.manualEstimatedLeaveDate ? "Выезд" : "Въезд"}, расчетный:</td>
                            <td className="text-right">
                                {dateTimeToView(
                                    item.startPoint.manualEstimatedLeaveDate || item.startPoint.estimatedDate,
                                )}
                            </td>
                        </tr>
                        {item.startPoint.type !== bidPointTypesEnum.StartPoint && (
                            <>
                                <tr>
                                    <td>{timeAtPointLabel(item.startPoint.type)}, план:</td>
                                    <td className="text-right">{loadUnloadTime(item.startPoint, "plan")}</td>
                                </tr>
                                <tr>
                                    <td>{timeAtPointLabel(item.startPoint.type)}, факт:</td>
                                    <td className="text-right">{loadUnloadTime(item.startPoint, "fact")}</td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="pb-3">
                <div className="car-planning__point-address-container">
                    <span className="car-planning__point-address-container--title">
                        {pointAddress(item.endPoint.geozone)}{" "}
                        <small className="text-muted">{formatDateTimezone(item.endPoint)}</small>
                    </span>
                    <small className="b-timeline-point__city--type ml-3">{typeTitle(item.endPoint)}</small>
                </div>
                {shiftItems(item.endPoint)}
                <table className="table table-xs">
                    <tbody>
                        <tr>
                            <td className="noborder">Въезд, план:</td>
                            <td className="text-right noborder">{dateTimeToView(item.endPoint.planEnterDateOffset)}</td>
                        </tr>
                        <tr>
                            <td>Въезд, факт:</td>
                            <td className="text-right">
                                {dateTimeToView(item.endPoint.enteredAtByLogist || item.endPoint.autoEnteredAt)}
                            </td>
                        </tr>
                        <tr>
                            <td>Выезд, план:</td>
                            <td className="text-right">{dateTimeToView(item.endPoint.planLeaveDateOffset)}</td>
                        </tr>
                        <tr>
                            <td>Выезд, факт:</td>
                            <td className="text-right">
                                {dateTimeToView(item.endPoint.loadUnloadedAtByLogist || item.endPoint.autoLeftAt)}
                            </td>
                        </tr>
                        <tr>
                            <td>{item.endPoint.manualEstimatedLeaveDate ? "Выезд" : "Въезд"}, расчетный:</td>
                            <td
                                className={cn("text-right", {
                                    "text-danger font-weight-bold": item.endPoint.manualEstimatedLeaveDate
                                        ? moment(item.endPoint.manualEstimatedLeaveDate) >
                                          moment(item.endPoint.planLeaveDateOffset)
                                        : item.endPoint.estimatedDate
                                          ? moment(item.endPoint.estimatedDate) >
                                            moment(item.endPoint.planEnterDateOffset)
                                          : false,
                                })}
                            >
                                {dateTimeToView(item.endPoint.manualEstimatedLeaveDate || item.endPoint.estimatedDate)}
                            </td>
                        </tr>
                        <tr>
                            <td>{timeAtPointLabel(item.endPoint.type)}, план:</td>
                            <td className="text-right">{loadUnloadTime(item.endPoint, "plan")}</td>
                        </tr>
                        <tr>
                            <td>{timeAtPointLabel(item.endPoint.type)}, факт:</td>
                            <td className="text-right">{loadUnloadTime(item.endPoint, "fact")}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <table className="table table-xs">
                <thead>
                    <tr>
                        <th className="text-nowrap noborder">Показатели заявки</th>
                        <th className="text-right noborder">План</th>
                        <th className="text-right noborder">Факт</th>
                    </tr>
                </thead>
                <tbody>
                    {(!!item.bidData.planEmptyMileage || !!item.bidData.factEmptyMileage) && (
                        <tr>
                            <td className="text-nowrap">Порожний пробег, км</td>
                            <td className="text-right">
                                {formatNumber(item.bidData.planEmptyMileage, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </td>
                            <td className="text-right">
                                {formatNumber(item.bidData.factEmptyMileage, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </td>
                        </tr>
                    )}
                    <tr>
                        <td className="text-nowrap">Пробег, км</td>
                        <td className="text-right">
                            {formatNumber(item.bidData.planMileage, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </td>
                        <td className="text-right">
                            {formatNumber(item.bidData.factMileage, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </td>
                    </tr>
                    <tr>
                        <td className="text-nowrap">Стоимость, {organization?.currencyData?.name}</td>
                        <td className="text-right">
                            {formatNumber(item.bidData.planPrice, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </td>
                        <td className="text-right">
                            {formatNumber(item.bidData.factPrice, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </td>
                    </tr>
                    <tr>
                        <td className="text-nowrap">{upperFirst(organization?.currencyData?.name)}/км</td>
                        <td className="text-right">
                            {formatNumber(item.bidData.planOutput, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </td>
                        <td className="text-right">
                            {formatNumber(item.bidData.factOutput, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </td>
                    </tr>
                    <tr>
                        <td className="text-nowrap">{upperFirst(organization?.currencyData?.name)}/час</td>
                        <td className="text-right">
                            {formatNumber(item.bidData.planOutputPerHour, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </td>
                        <td className="text-right">
                            {formatNumber(item.bidData.factOutputPerHour, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </td>
                    </tr>
                </tbody>
            </table>
        </>
    );
};
